import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canAccess, firstAllowedPath, TOOLS_PERMS } from "@/lib/perms";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Channels from "@/pages/Channels";
import Products from "@/pages/Products";
import Settlements from "@/pages/Settlements";
import Fees from "@/pages/Fees";
import Prices from "@/pages/Prices";
import Assistant from "@/pages/Assistant";
import McpGuide from "@/pages/McpGuide";
import Integrations from "@/pages/Integrations";
import ApiKeys from "@/pages/ApiKeys";
import AmazonTools from "@/pages/AmazonTools";
import UsersPage from "@/pages/Users";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" data-testid="auth-loading" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const Guard = ({ perm, admin, children }) => {
  const { user } = useAuth();
  const ok = admin ? user?.role === "admin" : canAccess(user, perm);
  if (ok) return children;
  const fallback = firstAllowedPath(user);
  if (fallback) return <Navigate to={fallback} replace />;
  return (
    <div className="p-10 text-center text-sm text-muted-foreground" data-testid="no-access">
      You don't have access to any features yet — ask your admin to grant permissions.
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><Layout /></Protected>}>
            <Route index element={<Guard perm="overview"><Overview /></Guard>} />
            <Route path="channels" element={<Guard perm="channels"><Channels /></Guard>} />
            <Route path="products" element={<Guard perm="products"><Products /></Guard>} />
            <Route path="settlements" element={<Guard perm="settlements"><Settlements /></Guard>} />
            <Route path="fees" element={<Guard perm="mis"><Fees /></Guard>} />
            <Route path="prices" element={<Guard perm="prices"><Prices /></Guard>} />
            <Route path="assistant" element={<Guard perm="assistant"><Assistant /></Guard>} />
            <Route path="mcp" element={<Guard perm="mcp"><McpGuide /></Guard>} />
            <Route path="integrations" element={<Guard perm="integrations"><Integrations /></Guard>} />
            <Route path="keys" element={<Guard perm="api_keys"><ApiKeys /></Guard>} />
            <Route path="tools" element={<Guard perm={TOOLS_PERMS}><AmazonTools /></Guard>} />
            <Route path="users" element={<Guard admin><UsersPage /></Guard>} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
